// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, EMPTY, forkJoin, merge, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, concatAll, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { flatten } from "ramda"
import * as TimelineActions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineStatsSlice from "../slices/stats"
import {
  ListDaySummariesRequest,
  ListDaySummariesResponse,
  ListTimelinesRequest,
  ListTimelinesResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { RootState } from "app/store"
import { planTimelineFetchOperations, TimelineFetchOperations } from "./lib/timelineFetchOperations"
import { DateTime } from "luxon"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { patchDevelopmentUrls } from "../devUrls"
import { DateRange, TimelineDate } from "../dateTime"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { ToastOptions } from "react-toastify/dist/types"
import { processTimesheetFieldData } from "./lib/processTimesheetFieldData"

const prepareTimelineFetchOperations = (action: { payload: TimelineDate | undefined }, state: RootState): TimelineFetchOperations =>
  planTimelineFetchOperations(state, DateTime.local)(action.payload)

type ListTimelinesRpc = (request: ListTimelinesRequest) => Observable<ListTimelinesResponse>
type ListDaySummariesRpc = (request: ListDaySummariesRequest) => Observable<ListDaySummariesResponse>

export const getTimelineData = (
  listTimelinesRpc: ListTimelinesRpc,
  listDaySummariesRpc: ListDaySummariesRpc,
) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.getTimelineData.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const fetchOperations = prepareTimelineFetchOperations(action, state)
        const rangeOfRequestedTimeline: DateRange = {
          startDateLocal: fetchOperations.requestedTimeline,
          endDateLocal: fetchOperations.requestedTimeline,
        }
        const actions = merge(
          listTimelines({
            listTimelinesRpc,
            requestedTimeline: rangeOfRequestedTimeline,
            ignoreError: false,
            isActiveDate: true,
            state,
          }),
          listDaySummaries(listDaySummariesRpc, fetchOperations.daySummaries, false),
          3,
        )
        return concat(
          // Fetch the requested timeline data, in parallel. If the requested
          // timeline data calls fail, the whole epic should fail, and show a message
          // to the user saying that we were unable to fetch their timeline data
          actions,
          // When responses for all are back, fetch the extra timelines (there will be up to 2) in
          // parallel. If the extra timelines call fails, don't display an error. This is non-fatal
          forkJoin(
            flatten(
              fetchOperations.extraTimelines.map(extraTimeline => [
                listTimelines({
                  listTimelinesRpc,
                  requestedTimeline: extraTimeline,
                  ignoreError: true,
                  state,
                }),
              ])
            )
          ).pipe(concatAll()),
        )
      }),
    )

const options: ToastOptions = {
  autoClose: 10000,
}

type ListTimelinesPayload = {
  listTimelinesRpc: ListTimelinesRpc,
  requestedTimeline: DateRange,
  ignoreError: boolean,
  isActiveDate?: boolean,
  state: RootState,
}
const listTimelines = ({
  listTimelinesRpc,
  requestedTimeline,
  ignoreError,
  isActiveDate,
  state,
}: ListTimelinesPayload): Observable<Action> =>
  of(requestedTimeline).pipe(
    map(r => new ListTimelinesRequest()
      .setStartDate(r.startDateLocal.toString())
      .setEndDate(r.endDateLocal.toString())
    ),
    mergeMap(listTimelinesRpc),
    map(r => r.toObject().timelinesList.map(t => patchDevelopmentUrls(t))),
    mergeMap(timelines => {
      const actions: Action[] = [
        timelines.length === 0 && isActiveDate
          ? TimelineSlice.resetLoadingAndRefreshRequired()
          : TimelineSlice.setTimeline(timelines),
      ]
      const timeGroups = flatten(timelines.map(t => t.timeGroupsInRangeList))
      const timeRows = flatten(timelines.map(t => t.timeRowsInRangeList))
      const timesheetFieldActions = processTimesheetFieldData(timeGroups, timeRows, state)
      actions.push(...timesheetFieldActions)
      return concat(actions)
    }),
    catchError(_ => concat(
      ignoreError ? EMPTY : of(showSnackBar({
        message: t({ id: "msg_get_timeline_failed", message: "Failed to get the timeline. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
        messageType: "ERROR",
        options,
      })),
      of(TimelineSlice.setLoading(false)),
    ))
  )

const listDaySummaries = (listDaySummariesRpc: ListDaySummariesRpc, daySummaries: DateRange, ignoreError: boolean): Observable<Action> =>
  of(daySummaries).pipe(
    map(_ => new ListDaySummariesRequest()
      .setStartDate(daySummaries.startDateLocal.toString())
      .setEndDate(daySummaries.endDateLocal.toString())
    ),
    mergeMap(listDaySummariesRpc),
    map(t =>
      ({
        response: t.toObject(),
        range: daySummaries
      })),
    map(TimelineStatsSlice.setDaySummaries),
    catchError(_ => ignoreError ? EMPTY : of(showSnackBar({
      message: t({ id: "msg_get_day_summaries_failed", message: "Failed to get day summaries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
      options,
    }))
    )
  )

const listTimelinesRpc = (request: ListTimelinesRequest): Observable<ListTimelinesResponse> =>
  grpc(() => timelineClient.listTimelines(request, null))

const listDaySummariesRpc = (request: ListDaySummariesRequest): Observable<ListDaySummariesResponse> =>
  grpc(() => timelineClient.listDaySummaries(request, null))

export default getTimelineData(listTimelinesRpc, listDaySummariesRpc)
